"use client";

import { Input } from "@/components/rac/input";
import { isValidOtp } from "@/info/login";
import { type ChangeEvent, type KeyboardEvent } from "react";
import { Label } from "react-aria-components";

interface OtpInputProps {
	otp: string;
	onOtpChange: (otp: string) => void;
	onEnterPress?: () => void;
	isDisabled?: boolean;
	placeholder?: string;
	label?: string;
}

export function OtpInput({
	label = "OTP Code",
	otp,
	onOtpChange,
	onEnterPress,
	isDisabled = false,
	placeholder = "Enter 5-digit OTP",
}: OtpInputProps) {
	const handleOtpChange = (event: ChangeEvent<HTMLInputElement>) => {
		// Only allow digits and limit to 5 characters
		const cleaned = event.target.value.replace(/[^\d]/g, "").slice(0, 5);
		onOtpChange(cleaned);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter" && onEnterPress && otp.length === 5) {
			event.preventDefault();
			onEnterPress();
		}
	};

	const isValid = otp.length === 0 || isValidOtp(otp);

	return (
		<div className="flex flex-col gap-1">
			<Label className="text-sm font-medium">{label}</Label>
			<div className="relative">
				<Input
					type="text"
					value={otp}
					onChange={handleOtpChange}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className={`w-full h-12 px-3 py-3 text-sm border rounded-lg transition-all duration-200 touch-manipulation text-left tracking-widest focus:outline-none focus:ring-2 focus:ring-primary-600 ${
						isValid ? "border-gray-300" : "border-red-300"
					} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
					disabled={isDisabled}
					autoComplete="one-time-code"
					inputMode="numeric"
					maxLength={5}
				/>
			</div>
			{!isValid && otp.length > 0 && <p className="text-xs text-red-600 mt-1">Please enter a valid 5-digit OTP</p>}
		</div>
	);
}
